// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { PlayerRow } from "@suns/api/generated-client/apollo";
import {
  groupMeasurements,
  KeySkillsTable,
  PlayerComparisons,
  PlayerMeasurements,
} from "./components";
import { apolloApi } from "@/shared/api";
import { useAsync } from "@/shared/hooks";
import {
  Badge,
  cn,
  Flex,
  Skeleton,
  SkeletonHeading,
  Text,
} from "@suns/design-system";
import { PlayerMedical } from "../PlayerOverview/components/PlayerMedical";
import { percentileClassname } from "./utils";

async function healthAndPerformanceLoader(playerId: number) {
  const { medical } = await apolloApi.getPlayerMedical(playerId);
  const performance = await apolloApi.getPlayerPerformanceMetrics(playerId);

  return {
    medical,
    performance,
  };
}

export function PlayerHealthAndPerformance({
  player,
  print = false,
}: {
  player: PlayerRow;
  print?: boolean;
}) {
  const { loading, response, error } = useAsync(
    healthAndPerformanceLoader,
    player.id
  );

  if (loading) {
    return <PlayerHealthAndPerformanceSkeleton />;
  }

  if (error) {
    throw error;
  }

  const {
    measurementPercentiles,
    keySkillsPercentiles,
    summary,
    playerComparisons,
  } = response.performance;

  const groupedMeasurementPercentiles = measurementPercentiles
    ? groupMeasurements(measurementPercentiles, true)
    : null;

  const allMeasurements = groupMeasurements(player.measurements);

  const badgeClassname = cn("border-black/5", print && "text-3xs");

  const hasKeySkills =
    keySkillsPercentiles?.rimProtection ||
    keySkillsPercentiles?.perimeterDefense ||
    keySkillsPercentiles?.agility ||
    keySkillsPercentiles?.athleticism;

  const hasPlayerComparisons =
    playerComparisons && Object.values(playerComparisons).some(Boolean);

  return (
    <Flex direction="down" gap="lg">
      {groupedMeasurementPercentiles &&
      groupedMeasurementPercentiles.length > 0 ? (
        <Flex direction="down" gap="sm">
          <Text heading muted size="md">
            Measurements
          </Text>
          <PlayerMeasurements
            measurements={groupedMeasurementPercentiles}
            hideSource
            hideMeasuredAt
            caption={
              <>
                The table above shows the players most recent official
                measurements. Each player is ranked by percentile based on
                current NBA players at the same position.`
              </>
            }
            print={print}
          />
          <Flex gap="sm">
            <Badge className={`${percentileClassname(1)} ${badgeClassname}`}>
              {"< 10th percentile"}
            </Badge>
            <Badge className={`${percentileClassname(11)} ${badgeClassname}`}>
              {"< 20th percentile"}
            </Badge>
            <Badge className={`${percentileClassname(81)} ${badgeClassname}`}>
              {"> 80th percentile"}
            </Badge>
            <Badge className={`${percentileClassname(91)} ${badgeClassname}`}>
              {"> 90th percentile"}
            </Badge>
          </Flex>
        </Flex>
      ) : allMeasurements && allMeasurements.length > 0 ? (
        <Flex direction="down" gap="sm">
          <Text heading muted size="md">
            Measurements
          </Text>
          <PlayerMeasurements
            measurements={allMeasurements}
            hideMeasuredAt
            print={print}
          />
        </Flex>
      ) : (
        <Text size="md" muted>
          No measurements available.
        </Text>
      )}
      {!print && allMeasurements.length > 2 && (
        <Flex direction="down" gap="sm">
          <Text heading muted size="md">
            Measurement Sources
          </Text>
          <PlayerMeasurements measurements={allMeasurements} />
        </Flex>
      )}
      {hasKeySkills && (
        <Flex direction="down" gap="sm">
          <Text heading muted size="md">
            Key Skills
          </Text>
          <KeySkillsTable
            rimProtection={keySkillsPercentiles.rimProtection}
            perimeterDefense={keySkillsPercentiles.perimeterDefense}
            agility={keySkillsPercentiles.agility}
            athleticism={keySkillsPercentiles.athleticism}
            print={print}
          />
        </Flex>
      )}
      {summary && (
        <Flex direction="down" gap="sm">
          <Text heading muted size="md">
            Performance Summary
          </Text>
          <Text size={print ? "2xs" : "md"}>{summary}</Text>
        </Flex>
      )}
      {hasPlayerComparisons && (
        <Flex direction="down" gap="sm">
          <Text heading muted size="md">
            Player Comparisons
          </Text>
          <PlayerComparisons comparisons={playerComparisons} print={print} />
        </Flex>
      )}
      {response?.medical?.notes && (
        <PlayerMedical
          print={print}
          medical={response.medical}
          title="Medical Notes"
        />
      )}
    </Flex>
  );
}

function PlayerHealthAndPerformanceSkeleton() {
  return (
    <Flex direction="down" gap="md">
      <SkeletonHeading className="h-10" />
      <Skeleton className="h-10 w-full" />
    </Flex>
  );
}
